import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/slices';
import { Label } from '@common/Label';
import { NodeField } from '@components/Flow/Nodes';
import { useQuery } from '@tanstack/react-query';
import { getMessagingProfiles, TelnyxMessagingProfile } from '@requests/telnyx';
import { Alert } from '@mui/material';
import { Button } from '@common/Button';
import { useTheme } from '../../../styles';
import { MessagingProfiles } from './MessagingProfilesAutocomplete';
import { useDispatch } from 'react-redux';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const MessagingProfile: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;
  const { label, description } = field;

  const {
    data: messagingProfiles = [],
    isLoading: profilesLoading,
    error,
    refetch,
  } = useQuery<TelnyxMessagingProfile[], RequestError>({
    queryKey: ['messaging_profiles'],
    queryFn: getMessagingProfiles,
    retry: false,
  });

  const errorMessage = error?.response?.data.error || error?.message;
  const value = state.messaging_profile_id;
  const noProfile = !value;

  const updateField = (value: string) => {
    const nodeId = command.node.id;
    const fieldId = field.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId, value }));
    dispatch(tabActions.setSaved(false));
  };

  const portalHost = window.location.origin.includes('dev')
    ? 'https://portaldev.telnyx.com/#'
    : 'https://portal.telnyx.com/#';

  return (
    <Container>
      <Label title={label} tooltip={description} />
      {noProfile && (
        <Alert severity='info' sx={{ mb: 1 }}>
          To setup an inbound message workflow, you must first assign a messaging profile to this trigger node. This
          profile is configured from the Telnyx mission control portal{' '}
          <a
            style={{ color: theme.primary }}
            href={
              window.location.origin.includes('dev')
                ? 'https://portaldev.telnyx.com/#/programmable-messaging/profiles'
                : 'https://portal.telnyx.com/#/programmable-messaging/profiles'
            }
            target='_blank'
            rel='noreferrer'
          >
            here
          </a>
          . You must have a different messaging profile for each workflow, but you can configure many numbers for a
          single messaging profile.
        </Alert>
      )}
      {errorMessage ? (
        <Container>
          <Alert
            severity='error'
            action={
              <Button small customColor='red' onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {errorMessage}
          </Alert>
        </Container>
      ) : messagingProfiles.length === 0 && !profilesLoading ? (
        <Container>
          <Alert
            severity='error'
            action={
              <Button small customColor='red' onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            <p>
              There were no messaging profiles found in your account. You can create profiles{' '}
              <a
                href={`${portalHost}/programmable-messaging/profiles`}
                target='_blank'
                rel='noreferrer'
                style={{ color: theme.primary }}
              >
                here
              </a>
              .
            </p>
          </Alert>
        </Container>
      ) : (
        <MessagingProfiles
          command={command}
          value={value}
          loading={profilesLoading}
          labels={messagingProfiles.map((profile, index) => ({
            id: index.toString(),
            label: profile.name,
            value: profile.id,
            metadata: profile,
          }))}
          onChange={(e, newValue) => updateField(newValue.value)}
          placeholder='Messaging Profile'
        />
      )}
    </Container>
  );
};
